import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { travelAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { AttendanceStatus } from '../../types/event';
import type { EventTravel, SaveEventTravelRequest, TravelCopyCandidate, Vehicle } from '../../types/travel';
import { TRAVEL_EXTRA_ROLES, formatTravelTime } from '../../types/travel';
import DeleteConfirmModal from '../ui/DeleteConfirmModal';
import { getErrorMessage } from '../../utils/errorMessage';

interface TravelEditorModalProps {
  travel: EventTravel;
  onClose: () => void;
  onSaved: (travel: EventTravel) => void;
  onDeleted: () => void;
  /** Ha közben más is módosította: a friss állapotból újranyitja a szerkesztőt. */
  onReload?: () => void;
}

interface DraftStop {
  key: string;
  name: string;
  address: string;
  /** datetime-local formátum: "2026-10-04T07:30" (üres = nincs megadva). */
  meetTime: string;
}

interface DraftVehicle {
  key: string;
  /** Az elmentett jármű azonosítója (így marad meg, melyik szülő ajánlotta fel). */
  existingId: number | null;
  /** Egy szülő ajánlotta fel - a neve. */
  ownerName: string | null;
  isOffered: boolean;
  vehicleId: number | null;
  name: string;
  seats: number;
  driverName: string;
  notes: string;
  stops: DraftStop[];
}

interface DraftPassenger {
  key: string;
  playerId: number | null;
  name: string;
  role: string;
  response?: AttendanceStatus | null;
  /** Hamis, ha már nincs a keretben (korábbi beosztás miatt látszik). */
  inRoster?: boolean;
  goesDirectly: boolean;
  /** Jelezte (vagy a stáb állította), hogy fuvar kell neki - beosztatlanul is megmarad. */
  needsRide: boolean;
  vehicleKey: string | null;
  stopKey: string | null;
  notes: string;
}

type Filter = 'all' | 'unassigned' | 'attending';

let keySeq = 0;
const newKey = (prefix: string) => `${prefix}-new-${Date.now()}-${keySeq++}`;

const toInputTime = (value?: string | null) => (value ? value.slice(0, 16) : '');
const fromInputTime = (value: string) => (value ? (value.length === 16 ? `${value}:00` : value) : null);

/** Egy órával a kezdés előtt: ésszerű kiinduló időpont egy új megállóhoz. */
const defaultMeetTime = (eventStart: string) => {
  const date = new Date(eventStart);
  date.setHours(date.getHours() - 1);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * A választó értéke: '' = nincs jelzés, 'need' = fuvarra vár, 'direct' = helyszínre,
 * 'jármű|megálló' = járművel.
 */
const assignmentValue = (p: DraftPassenger) =>
  p.goesDirectly ? 'direct' : p.vehicleKey ? `${p.vehicleKey}|${p.stopKey ?? ''}` : p.needsRide ? 'need' : '';

/** Eltolás egy datetime-local értéken (másoláskor a megállók időpontjára). */
const shiftInputTime = (value: string, deltaMs: number) => {
  if (!value) return '';
  const date = new Date(new Date(value).getTime() + deltaMs);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const responseBadge = (status?: AttendanceStatus | null) => {
  switch (status) {
    case AttendanceStatus.Attending:
      return { text: 'Jön', className: 'bg-green-100 text-green-800' };
    case AttendanceStatus.NotAttending:
      return { text: 'Nem jön', className: 'bg-red-100 text-red-800' };
    case AttendanceStatus.Maybe:
      return { text: 'Talán', className: 'bg-yellow-100 text-yellow-800' };
    default:
      return null;
  }
};

// Mobilon 16px, különben az iOS ránagyít a mezőre
const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Az utazás szervezése: járművek (a klub járművei közül vagy egyedi, pl. szülő
 * autója), járművenként a megállók időponttal, és hogy ki melyik járművel,
 * melyik megállóból utazik, vagy egyenesen a helyszínre megy. A játékosok a
 * keretből jönnek, mellettük plusz személyek (szülő, szertáros...) vehetők fel.
 */
const TravelEditorModal: React.FC<TravelEditorModalProps> = ({ travel, onClose, onSaved, onDeleted, onReload }) => {
  const { showToast } = useToast();
  const [tab, setTab] = useState<'vehicles' | 'passengers'>(travel.exists ? 'passengers' : 'vehicles');
  const [fleet, setFleet] = useState<Vehicle[]>([]);
  const [notes, setNotes] = useState(travel.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState('');
  const [conflict, setConflict] = useState<string | null>(null);
  const [copySources, setCopySources] = useState<TravelCopyCandidate[]>([]);
  const [importing, setImporting] = useState(false);

  const [vehicles, setVehicles] = useState<DraftVehicle[]>(() =>
    travel.vehicles.map((v) => ({
      key: `v${v.id}`,
      existingId: v.id,
      ownerName: v.ownerName ?? null,
      isOffered: !!v.isOffered,
      vehicleId: v.vehicleId ?? null,
      name: v.name,
      seats: v.seats,
      driverName: v.driverName ?? '',
      notes: v.notes ?? '',
      stops: v.stops.map((s) => ({
        key: `s${s.id}`,
        name: s.name,
        address: s.address ?? '',
        meetTime: toInputTime(s.meetTime),
      })),
    }))
  );

  const [passengers, setPassengers] = useState<DraftPassenger[]>(() => {
    const fromSaved = (p: EventTravel['passengers'][number]) => ({
      goesDirectly: p.goesDirectly,
      needsRide: !p.goesDirectly && !p.travelVehicleId,
      vehicleKey: !p.goesDirectly && p.travelVehicleId ? `v${p.travelVehicleId}` : null,
      stopKey: !p.goesDirectly && p.travelVehicleId && p.travelStopId ? `s${p.travelStopId}` : null,
      notes: p.notes ?? '',
    });

    const players: DraftPassenger[] = travel.roster.map((r) => {
      const saved = travel.passengers.find((p) => p.playerId === r.playerId);
      return {
        key: `p${r.playerId}`,
        playerId: r.playerId,
        name: r.name,
        role: '',
        response: r.response,
        inRoster: r.inRoster !== false,
        ...(saved
          ? fromSaved(saved)
          : { goesDirectly: false, needsRide: false, vehicleKey: null, stopKey: null, notes: '' }),
      };
    });

    const extras: DraftPassenger[] = travel.passengers
      .filter((p) => !p.playerId)
      .map((p) => ({
        key: `x${p.id}`,
        playerId: null,
        name: p.name,
        role: p.role ?? '',
        ...fromSaved(p),
      }));

    return [...players, ...extras];
  });

  useEffect(() => {
    travelAPI
      .getVehicles()
      .then(setFleet)
      .catch((err) => console.error('A járművek betöltése nem sikerült:', err));
    travelAPI
      .getCopySources(travel.eventId)
      .then(setCopySources)
      .catch((err) => console.error('A korábbi utazások betöltése nem sikerült:', err));
  }, [travel.eventId]);

  // --- Járművek -----------------------------------------------------------

  const seatedIn = useMemo(() => {
    const counts: Record<string, number> = {};
    passengers.forEach((p) => {
      if (!p.goesDirectly && p.vehicleKey) counts[p.vehicleKey] = (counts[p.vehicleKey] ?? 0) + 1;
    });
    return counts;
  }, [passengers]);

  const overbooked = vehicles.filter((v) => (seatedIn[v.key] ?? 0) > v.seats);

  const addVehicle = (value: string) => {
    if (!value) return;
    const fromFleet = value === 'custom' ? null : fleet.find((v) => v.id === Number(value));
    setVehicles((prev) => [
      ...prev,
      {
        key: newKey('v'),
        existingId: null,
        ownerName: null,
        isOffered: false,
        vehicleId: fromFleet?.id ?? null,
        name: fromFleet?.name ?? '',
        seats: fromFleet?.seats ?? 4,
        driverName: '',
        notes: '',
        stops: [],
      },
    ]);
  };

  const updateVehicle = (key: string, patch: Partial<DraftVehicle>) => {
    setVehicles((prev) => prev.map((v) => (v.key === key ? { ...v, ...patch } : v)));
  };

  const removeVehicle = (key: string) => {
    setVehicles((prev) => prev.filter((v) => v.key !== key));
    // Akik benne ültek, újra fuvarra várnak
    setPassengers((prev) =>
      prev.map((p) => (p.vehicleKey === key ? { ...p, vehicleKey: null, stopKey: null, needsRide: true } : p))
    );
  };

  const moveVehicle = (index: number, delta: number) => {
    setVehicles((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addStop = (vehicleKey: string) => {
    setVehicles((prev) =>
      prev.map((v) => {
        if (v.key !== vehicleKey) return v;
        // Az előző megálló után fél órával, különben egy órával a kezdés előtt
        const last = v.stops[v.stops.length - 1];
        let meetTime = defaultMeetTime(travel.eventStart);
        if (last?.meetTime) {
          const date = new Date(last.meetTime);
          date.setMinutes(date.getMinutes() + 30);
          const pad = (n: number) => n.toString().padStart(2, '0');
          meetTime = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
        }
        return { ...v, stops: [...v.stops, { key: newKey('s'), name: '', address: '', meetTime }] };
      })
    );
  };

  const updateStop = (vehicleKey: string, stopKey: string, patch: Partial<DraftStop>) => {
    setVehicles((prev) =>
      prev.map((v) =>
        v.key === vehicleKey
          ? { ...v, stops: v.stops.map((s) => (s.key === stopKey ? { ...s, ...patch } : s)) }
          : v
      )
    );
  };

  const removeStop = (vehicleKey: string, stopKey: string) => {
    setVehicles((prev) =>
      prev.map((v) => (v.key === vehicleKey ? { ...v, stops: v.stops.filter((s) => s.key !== stopKey) } : v))
    );
    setPassengers((prev) => prev.map((p) => (p.stopKey === stopKey ? { ...p, stopKey: null } : p)));
  };

  const moveStop = (vehicleKey: string, index: number, delta: number) => {
    setVehicles((prev) =>
      prev.map((v) => {
        if (v.key !== vehicleKey) return v;
        const target = index + delta;
        if (target < 0 || target >= v.stops.length) return v;
        const stops = [...v.stops];
        [stops[index], stops[target]] = [stops[target], stops[index]];
        return { ...v, stops };
      })
    );
  };

  // --- Utasok -------------------------------------------------------------

  // Az opciókban a jármű neve is szerepel, mert a kiválasztott értéknél a böngésző
  // csak az opció szövegét mutatja, a csoport (jármű) nevét nem
  const assignmentOptions = useMemo(() => {
    const options: { value: string; label: string; group: string; vehicleKey: string }[] = [];
    vehicles.forEach((v) => {
      const name = v.name.trim() || 'Névtelen jármű';
      const seated = seatedIn[v.key] ?? 0;
      const capacity = `${seated}/${v.seats}${seated >= v.seats ? ' – tele' : ''}`;
      const group = `${name} (${capacity})`;
      if (v.stops.length === 0) {
        options.push({ value: `${v.key}|`, label: `${name} (${capacity})`, group, vehicleKey: v.key });
      } else {
        v.stops.forEach((s) => {
          const time = s.meetTime ? ` · ${formatTravelTime(s.meetTime)}` : '';
          options.push({
            value: `${v.key}|${s.key}`,
            label: `${name} · ${s.name.trim() || 'Névtelen megálló'}${time} (${capacity})`,
            group,
            vehicleKey: v.key,
          });
        });
        options.push({ value: `${v.key}|`, label: `${name} · megálló nélkül (${capacity})`, group, vehicleKey: v.key });
      }
    });
    return options;
  }, [vehicles, seatedIn]);

  /** Hány szabad hely kellene a járműben, ha a megadott utasokat beültetnénk. */
  const neededSeats = (vehicleKey: string, keys: string[]) =>
    passengers.filter((p) => keys.includes(p.key) && (p.goesDirectly || p.vehicleKey !== vehicleKey)).length;

  const freeSeats = (vehicleKey: string) => {
    const v = vehicles.find((x) => x.key === vehicleKey);
    return v ? v.seats - (seatedIn[vehicleKey] ?? 0) : 0;
  };

  const applyAssignment = (p: DraftPassenger, value: string): DraftPassenger => {
    if (value === 'direct') return { ...p, goesDirectly: true, needsRide: false, vehicleKey: null, stopKey: null };
    if (value === 'need') return { ...p, goesDirectly: false, needsRide: true, vehicleKey: null, stopKey: null };
    if (!value) return { ...p, goesDirectly: false, needsRide: false, vehicleKey: null, stopKey: null };
    const [vehicleKey, stopKey] = value.split('|');
    return { ...p, goesDirectly: false, needsRide: true, vehicleKey, stopKey: stopKey || null };
  };

  /**
   * Egy másik esemény utazásának átvétele: a stáb járművei a megállókkal (az
   * időpontok ehhez az eseményhez igazítva), és akik ott ültek, itt is - ha a
   * keretben vannak, nem mondták le, és még nem döntöttek másképp.
   */
  const importFrom = async (sourceEventId: number) => {
    setImporting(true);
    try {
      const source = await travelAPI.getTravel(sourceEventId);
      const delta = new Date(travel.eventStart).getTime() - new Date(source.eventStart).getTime();
      const vehicleKeys: Record<number, string> = {};
      const stopKeys: Record<number, string> = {};

      const imported: DraftVehicle[] = source.vehicles
        .filter((v) => !v.isOffered)
        .map((v) => {
          const key = newKey('v');
          vehicleKeys[v.id] = key;
          return {
            key,
            existingId: null,
            ownerName: null,
            isOffered: false,
            vehicleId: v.vehicleId ?? null,
            name: v.name,
            seats: v.seats,
            driverName: v.driverName ?? '',
            notes: v.notes ?? '',
            stops: v.stops.map((s) => {
              const stopKey = newKey('s');
              stopKeys[s.id] = stopKey;
              return { key: stopKey, name: s.name, address: s.address ?? '', meetTime: shiftInputTime(toInputTime(s.meetTime), delta) };
            }),
          };
        });

      setVehicles((prev) => [...imported, ...prev]);
      setPassengers((prev) =>
        prev.map((p) => {
          if (!p.playerId || p.goesDirectly || p.vehicleKey || p.response === AttendanceStatus.NotAttending || p.inRoster === false) {
            return p;
          }
          const original = source.passengers.find(
            (x) => x.playerId === p.playerId && x.travelVehicleId != null && vehicleKeys[x.travelVehicleId] != null
          );
          if (!original) return p;
          return {
            ...p,
            needsRide: true,
            vehicleKey: vehicleKeys[original.travelVehicleId!],
            stopKey: original.travelStopId ? stopKeys[original.travelStopId] ?? null : null,
          };
        })
      );
      showToast(`${imported.length} jármű átvéve a megállókkal. Ellenőrizd, majd mentsd el.`);
    } catch (err: any) {
      showToast(getErrorMessage(err, 'Az utazás átvétele nem sikerült.'), 'error');
    } finally {
      setImporting(false);
    }
  };

  const assign = (key: string, value: string) => {
    setPassengers((prev) => prev.map((p) => (p.key === key ? applyAssignment(p, value) : p)));
  };

  const updatePassenger = (key: string, patch: Partial<DraftPassenger>) => {
    setPassengers((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  };

  const addExtra = () => {
    const key = newKey('x');
    setPassengers((prev) => [
      ...prev,
      { key, playerId: null, name: '', role: '', goesDirectly: false, needsRide: false, vehicleKey: null, stopKey: null, notes: '' },
    ]);
    setFilter('all');
    setSearch('');
  };

  const removeExtra = (key: string) => {
    setPassengers((prev) => prev.filter((p) => p.key !== key));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // Aki jelezte, hogy nem jön, az utazásban sem szerepel (a jelzéskor ki is kerül belőle)
  const isDeclined = (p: DraftPassenger) =>
    !!p.playerId && p.response === AttendanceStatus.NotAttending && !p.goesDirectly && !p.vehicleKey && !p.needsRide;
  const declinedCount = passengers.filter(isDeclined).length;

  const visiblePassengers = passengers.filter((p) => {
    if (isDeclined(p)) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'unassigned') return !p.goesDirectly && !p.vehicleKey;
    if (filter === 'attending') return p.response === AttendanceStatus.Attending;
    return true;
  });

  const toggleSelected = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allVisibleSelected = visiblePassengers.length > 0 && visiblePassengers.every((p) => selected.has(p.key));

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visiblePassengers.forEach((p) => next.delete(p.key));
      else visiblePassengers.forEach((p) => next.add(p.key));
      return next;
    });
  };

  const applyBulk = () => {
    const keys = Array.from(selected);
    const vehicleKey = bulkTarget && bulkTarget !== 'direct' ? bulkTarget.split('|')[0] : null;
    if (vehicleKey) {
      const needed = neededSeats(vehicleKey, keys);
      const free = freeSeats(vehicleKey);
      if (needed > free) {
        const name = vehicles.find((v) => v.key === vehicleKey)?.name.trim() || 'A jármű';
        showToast(`${name}: ${needed} főnek kellene hely, de csak ${Math.max(0, free)} szabad.`, 'error');
        return;
      }
    }
    setPassengers((prev) => prev.map((p) => (selected.has(p.key) ? applyAssignment(p, bulkTarget) : p)));
    setSelected(new Set());
  };

  const unassignedCount = passengers.filter((p) => !p.goesDirectly && !p.vehicleKey && !isDeclined(p)).length;
  const needsRideCount = passengers.filter((p) => p.playerId && p.needsRide && !p.goesDirectly && !p.vehicleKey).length;
  const directCount = passengers.filter((p) => p.goesDirectly).length;

  // --- Mentés -------------------------------------------------------------

  const validationError = (): string | null => {
    const unnamedVehicle = vehicles.find((v) => !v.name.trim());
    if (unnamedVehicle) return 'Minden járműnek adj nevet.';
    if (vehicles.some((v) => !v.seats || v.seats < 1 || v.seats > 100)) return 'A férőhelyek száma 1 és 100 között lehet.';
    if (vehicles.some((v) => v.stops.some((s) => !s.name.trim()))) return 'Minden megállónak adj nevet.';
    if (passengers.some((p) => !p.playerId && !p.name.trim())) return 'Minden plusz személynek adj nevet.';
    if (overbooked.length > 0) {
      const v = overbooked[0];
      return `A(z) ${v.name.trim() || 'névtelen'} járműbe ${seatedIn[v.key]} utas van beosztva, de csak ${v.seats} hely van.`;
    }
    return null;
  };

  const handleSave = async () => {
    const error = validationError();
    if (error) {
      showToast(error, 'error');
      return;
    }

    const request: SaveEventTravelRequest = {
      notes: notes.trim() || null,
      baseUpdatedAt: travel.exists ? travel.updatedAt ?? null : null,
      checkConflicts: true,
      vehicles: vehicles.map((v) => ({
        key: v.key,
        existingId: v.existingId,
        vehicleId: v.vehicleId,
        name: v.name.trim(),
        seats: v.seats,
        driverName: v.driverName.trim() || null,
        notes: v.notes.trim() || null,
        stops: v.stops.map((s) => ({
          key: s.key,
          name: s.name.trim(),
          address: s.address.trim() || null,
          meetTime: fromInputTime(s.meetTime),
        })),
      })),
      passengers: passengers
        // Akiről nincs jelzés, azt nem kell elküldeni, a keret része marad
        .filter((p) => !p.playerId || p.goesDirectly || p.vehicleKey || p.needsRide)
        .map((p) => ({
          playerId: p.playerId,
          name: p.playerId ? null : p.name.trim(),
          role: p.playerId ? null : p.role.trim() || null,
          goesDirectly: p.goesDirectly,
          vehicleKey: p.goesDirectly ? null : p.vehicleKey,
          stopKey: p.goesDirectly ? null : p.stopKey,
          notes: p.notes.trim() || null,
        })),
    };

    setSaving(true);
    try {
      const saved = await travelAPI.saveTravel(travel.eventId, request);
      showToast('Utazás elmentve.');
      onSaved(saved);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setConflict(err.response.data?.message || 'Az utazást közben más is módosította.');
        return;
      }
      showToast(getErrorMessage(err, 'Az utazás mentése nem sikerült.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await travelAPI.deleteTravel(travel.eventId);
      showToast('Utazás törölve.');
      setConfirmDelete(false);
      onDeleted();
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A törlés nem sikerült.'), 'error');
    }
  };

  const usedFleetIds = new Set(vehicles.map((v) => v.vehicleId).filter((id): id is number => id != null));

  // --- Megjelenítés -------------------------------------------------------

  /** A választó; `forKeys` utasainak nem fér be a tele járműbe, az ilyen opció le van tiltva. */
  const renderAssignmentSelect = (value: string, onChange: (value: string) => void, label: string, forKeys: string[]) => {
    const groups = Array.from(new Set(assignmentOptions.map((o) => o.group)));
    const blocked = new Set(
      vehicles.filter((v) => neededSeats(v.key, forKeys) > freeSeats(v.key)).map((v) => v.key)
    );
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className={inputClass}>
        <option value="">Nincs jelzés</option>
        <option value="need">Fuvarra vár</option>
        <option value="direct">Egyenesen a helyszínre</option>
        {groups.map((group) => (
          <optgroup key={group} label={group}>
            {assignmentOptions
              .filter((o) => o.group === group)
              .map((o) => (
                <option key={o.value} value={o.value} disabled={blocked.has(o.vehicleKey) && o.value !== value}>
                  {o.label}
                </option>
              ))}
          </optgroup>
        ))}
      </select>
    );
  };

  const vehiclesTab = (
    <div className="space-y-4">
      <div>
        <label htmlFor="travel-notes" className="mb-1 block text-sm font-medium text-gray-700">
          Általános tudnivalók
        </label>
        <textarea
          id="travel-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          rows={2}
          placeholder="Pl. hazaindulás kb. 18:00-kor, mindenki hozzon kulacsot."
          className={inputClass}
        />
      </div>

      {vehicles.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          <TruckIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
          Még nincs jármű. Válassz a klub járművei közül, vagy vegyél fel egyedit (pl. egy szülő autóját).
        </div>
      )}

      {vehicles.map((vehicle, index) => {
        const seated = seatedIn[vehicle.key] ?? 0;
        const over = seated > vehicle.seats;
        return (
          <div key={vehicle.key} className="rounded-lg border border-gray-200 bg-gray-50 p-3 md:p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <TruckIcon className="h-5 w-5 text-gray-500" />
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    over ? 'bg-red-100 text-red-800' : 'bg-white text-gray-700 ring-1 ring-gray-200'
                  }`}
                >
                  {seated} / {vehicle.seats} hely
                </span>
                {vehicle.isOffered ? (
                  <span className="rounded bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-sky-200">
                    Felajánlotta: {vehicle.ownerName ?? 'egy szülő'}
                  </span>
                ) : (
                  vehicle.vehicleId == null && <span className="text-xs text-gray-500">Egyedi jármű</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveVehicle(index, -1)}
                  disabled={index === 0}
                  aria-label="Jármű feljebb"
                  className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronUpIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveVehicle(index, 1)}
                  disabled={index === vehicles.length - 1}
                  aria-label="Jármű lejjebb"
                  className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 disabled:opacity-30"
                >
                  <ChevronDownIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeVehicle(vehicle.key)}
                  aria-label="Jármű eltávolítása"
                  className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-600">Név</label>
                <input
                  value={vehicle.name}
                  onChange={(e) => updateVehicle(vehicle.key, { name: e.target.value })}
                  maxLength={100}
                  placeholder="Pl. Nagy csapatbusz, Kovács család autója"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Utashely</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={vehicle.seats || ''}
                  onChange={(e) => updateVehicle(vehicle.key, { seats: Number(e.target.value) })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Sofőr</label>
                <input
                  value={vehicle.driverName}
                  onChange={(e) => updateVehicle(vehicle.key, { driverName: e.target.value })}
                  maxLength={100}
                  className={inputClass}
                />
              </div>
              <div className="col-span-2 md:col-span-4">
                <label className="mb-1 block text-xs font-medium text-gray-600">Megjegyzés</label>
                <input
                  value={vehicle.notes}
                  onChange={(e) => updateVehicle(vehicle.key, { notes: e.target.value })}
                  maxLength={500}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Megállók</p>
              {vehicle.stops.length === 0 && (
                <p className="mb-2 text-xs text-gray-500">
                  Nincs megálló - az utasok a járműnél gyülekeznek. Vegyél fel megállót, ha útközben is vesz fel valakit.
                </p>
              )}
              <ol className="space-y-2">
                {vehicle.stops.map((stop, stopIndex) => (
                  <li key={stop.key} className="rounded-md border border-gray-200 bg-white p-2">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_12rem_auto]">
                      <input
                        value={stop.name}
                        onChange={(e) => updateStop(vehicle.key, stop.key, { name: e.target.value })}
                        maxLength={200}
                        placeholder={`${stopIndex + 1}. megálló neve`}
                        aria-label="Megálló neve"
                        className={inputClass}
                      />
                      <input
                        value={stop.address}
                        onChange={(e) => updateStop(vehicle.key, stop.key, { address: e.target.value })}
                        maxLength={300}
                        placeholder="Cím (nem kötelező)"
                        aria-label="Megálló címe"
                        className={inputClass}
                      />
                      <input
                        type="datetime-local"
                        value={stop.meetTime}
                        onChange={(e) => updateStop(vehicle.key, stop.key, { meetTime: e.target.value })}
                        aria-label="Mikorra kell ott lenni"
                        className={inputClass}
                      />
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => moveStop(vehicle.key, stopIndex, -1)}
                          disabled={stopIndex === 0}
                          aria-label="Megálló feljebb"
                          className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        >
                          <ChevronUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStop(vehicle.key, stopIndex, 1)}
                          disabled={stopIndex === vehicle.stops.length - 1}
                          aria-label="Megálló lejjebb"
                          className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        >
                          <ChevronDownIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeStop(vehicle.key, stop.key)}
                          aria-label="Megálló törlése"
                          className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
              <button
                type="button"
                onClick={() => addStop(vehicle.key)}
                className="mt-2 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50"
              >
                <PlusIcon className="h-4 w-4" />
                Megálló hozzáadása
              </button>
            </div>
          </div>
        );
      })}

      <div>
        <label htmlFor="add-vehicle" className="mb-1 block text-sm font-medium text-gray-700">
          Jármű hozzáadása
        </label>
        <select
          id="add-vehicle"
          value=""
          onChange={(e) => addVehicle(e.target.value)}
          className={`${inputClass} md:max-w-sm`}
        >
          <option value="">Válassz...</option>
          {fleet.length > 0 && (
            <optgroup label="A klub járművei">
              {fleet.map((v) => (
                <option key={v.id} value={v.id} disabled={usedFleetIds.has(v.id)}>
                  {v.name} ({v.seats} hely){usedFleetIds.has(v.id) ? ' - már hozzáadva' : ''}
                </option>
              ))}
            </optgroup>
          )}
          <option value="custom">Egyedi jármű (pl. szülő autója)</option>
        </select>
        {fleet.length === 0 && (
          <p className="mt-1 text-xs text-gray-500">
            A klub járműveit (pl. csapatbuszok) az Utazások oldal Járművek fülén lehet felvenni.
          </p>
        )}
      </div>

      {copySources.length > 0 && (
        <div>
          <label htmlFor="import-travel" className="mb-1 block text-sm font-medium text-gray-700">
            Átvétel egy másik eseményből
          </label>
          <select
            id="import-travel"
            value=""
            disabled={importing}
            onChange={(e) => e.target.value && importFrom(Number(e.target.value))}
            className={`${inputClass} md:max-w-sm`}
          >
            <option value="">{importing ? 'Betöltés...' : 'Válassz eseményt...'}</option>
            {copySources.map((c) => (
              <option key={c.eventId} value={c.eventId}>
                {formatTravelTime(c.eventStart, true)} · {c.eventTitle}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Pl. a múlt heti edzés kisbusza a megállókkal és az utasokkal; az időpontok ehhez az eseményhez igazodnak.
          </p>
        </div>
      )}
    </div>
  );

  const passengersTab = (
    <>
      {vehicles.length > 0 && (
        // Élő létszám járművenként, hogy beosztás közben látszódjon, hol van még hely
        <div className="sticky -top-4 z-10 -mx-4 mb-3 flex gap-2 overflow-x-auto border-b border-gray-100 bg-white/95 px-4 py-2 backdrop-blur md:-mx-6 md:px-6">
          {vehicles.map((v) => {
            const seated = seatedIn[v.key] ?? 0;
            const tone =
              seated > v.seats
                ? 'bg-red-100 text-red-800 ring-red-200'
                : seated === v.seats
                  ? 'bg-amber-100 text-amber-900 ring-amber-200'
                  : 'bg-gray-50 text-gray-800 ring-gray-200';
            return (
              <span key={v.key} className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-sm ring-1 ${tone}`}>
                <TruckIcon className="h-4 w-4 opacity-60" />
                <span className="max-w-[10rem] truncate font-medium">{v.name.trim() || 'Névtelen jármű'}</span>
                <span className="tabular-nums">
                  {seated}/{v.seats}
                </span>
                {seated >= v.seats && <span className="text-xs font-semibold">{seated > v.seats ? 'túltelt' : 'tele'}</span>}
              </span>
            );
          })}
        </div>
      )}

    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        {travel.rosterSource === 'squad'
          ? 'A játékosok a mérkőzés keretéből jönnek. Mindenki mást plusz személyként, névvel vehetsz fel.'
          : 'Ehhez az eseményhez még nincs meccskeret, ezért a teljes korosztály látszik. Ha a mérkőzésnél megadod a nevezést, csak a keret játékosai maradnak.'}
      </p>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span className="text-gray-700">
          <span className="font-semibold">{passengers.length - declinedCount - unassignedCount - directCount}</span> járművel
        </span>
        <span className="text-gray-700">
          <span className="font-semibold">{directCount}</span> egyenesen a helyszínre
        </span>
        <span className={needsRideCount > 0 ? 'text-amber-700' : 'text-gray-700'}>
          <span className="font-semibold">{needsRideCount}</span> fuvarra vár
        </span>
        <span className="text-gray-700">
          <span className="font-semibold">{unassignedCount - needsRideCount}</span> nem jelzett
        </span>
        {declinedCount > 0 && (
          <span className="text-gray-500" title="Akik jelezték, hogy nem jönnek, nem jelennek meg a listában">
            ({declinedCount} nem jön, ők nem látszanak)
          </span>
        )}
      </div>

      {vehicles.length === 0 && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Még nincs jármű - a Járművek fülön vegyél fel egyet, hogy utasokat oszthass be.
        </p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név alapján..."
            aria-label="Keresés az utasok között"
            className={`${inputClass} pl-9`}
          />
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {([
            ['all', 'Mind'],
            ['unassigned', 'Beosztatlan'],
            ['attending', 'Jön'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              aria-pressed={filter === key}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium ${
                filter === key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-col gap-2 rounded-md border border-primary-200 bg-primary-50 p-2 sm:flex-row sm:items-center">
          <span className="text-sm font-medium text-primary-900">{selected.size} kijelölve:</span>
          <div className="flex-1">{renderAssignmentSelect(bulkTarget, setBulkTarget, 'Kijelöltek beosztása', Array.from(selected))}</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyBulk}
              className="flex-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 sm:flex-none"
            >
              Beosztás
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 sm:flex-none"
            >
              Mégse
            </button>
          </div>
        </div>
      )}

      <div className="rounded-md border border-gray-200 bg-white">
        <label className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 text-xs font-medium text-gray-600">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={toggleAllVisible}
            disabled={visiblePassengers.length === 0}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Összes kijelölése ({visiblePassengers.length})
        </label>
        {visiblePassengers.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">Nincs a szűrésnek megfelelő utas.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {visiblePassengers.map((p) => {
              const badge = responseBadge(p.response);
              const isExtra = !p.playerId;
              return (
                <li key={p.key} className="flex items-start gap-2 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(p.key)}
                    onChange={() => toggleSelected(p.key)}
                    aria-label={`${p.name || 'Plusz személy'} kijelölése`}
                    className="mt-3 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,16rem)]">
                    {isExtra ? (
                      <div className="flex gap-2">
                        <input
                          value={p.name}
                          onChange={(e) => updatePassenger(p.key, { name: e.target.value })}
                          maxLength={100}
                          placeholder="Név"
                          aria-label="Plusz személy neve"
                          className={inputClass}
                        />
                        <input
                          value={p.role}
                          onChange={(e) => updatePassenger(p.key, { role: e.target.value })}
                          maxLength={50}
                          list="travel-extra-roles"
                          placeholder="Szerep"
                          aria-label="Szerep"
                          className={`${inputClass} max-w-[9rem]`}
                        />
                      </div>
                    ) : (
                      <div className="min-w-0 self-center">
                        <span className="text-sm font-medium text-gray-900">{p.name}</span>
                        {p.inRoster === false && (
                          <span
                            className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                            title="Korábban be lett osztva, de azóta nincs a meccs keretében"
                          >
                            Nincs a keretben
                          </span>
                        )}
                        {badge && (
                          <span className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-medium ${badge.className}`}>
                            {badge.text}
                          </span>
                        )}
                      </div>
                    )}
                    {renderAssignmentSelect(assignmentValue(p), (value) => assign(p.key, value), `${p.name || 'Plusz személy'} utazása`, [p.key])}
                  </div>
                  {isExtra && (
                    <button
                      type="button"
                      onClick={() => removeExtra(p.key)}
                      aria-label="Plusz személy törlése"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <datalist id="travel-extra-roles">
        {TRAVEL_EXTRA_ROLES.map((role) => (
          <option key={role} value={role} />
        ))}
      </datalist>

      <button
        type="button"
        onClick={addExtra}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50"
      >
        <UserPlusIcon className="h-4 w-4" />
        Plusz személy (szülő, néző, szertáros...)
      </button>
    </div>
    </>
  );

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <Dialog.Panel className="modal-panel-mobile flex w-full flex-col bg-white shadow-xl md:max-h-[90vh] md:max-w-4xl md:rounded-lg">
          <div className="flex shrink-0 items-start justify-between gap-2 border-b p-4 md:px-6">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Utazás szervezése</Dialog.Title>
              <p className="truncate text-sm text-gray-500">
                {travel.eventTitle} · {formatTravelTime(travel.eventStart, true)}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Bezárás"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex shrink-0 gap-1 border-b border-gray-200 px-4 md:px-6" role="tablist">
            {([
              ['vehicles', `Járművek és megállók (${vehicles.length})`],
              ['passengers', `Utasok (${passengers.length})`],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
                  tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {conflict && (
            <div className="flex shrink-0 flex-col gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between md:px-6">
              <span>{conflict}</span>
              {onReload && (
                <button
                  type="button"
                  onClick={onReload}
                  className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                >
                  Újratöltés
                </button>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 md:px-6">{tab === 'vehicles' ? vehiclesTab : passengersTab}</div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t p-4 pb-safe-4 sm:flex-row sm:items-center sm:justify-between md:px-6">
            <div>
              {travel.exists && (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="w-full rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 sm:w-auto"
                >
                  Utazás törlése
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {overbooked.length > 0 && (
                <p className="text-xs text-red-600">
                  Túltelt: {overbooked.map((v) => v.name.trim() || 'névtelen jármű').join(', ')}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 sm:flex-none"
                >
                  Mégse
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 sm:flex-none"
                >
                  {saving ? 'Mentés...' : 'Mentés'}
                </button>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>

      <DeleteConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Utazás törlése"
        message="Biztosan törlöd az utazás szervezését? A járművek beosztása, a megállók és a plusz személyek is törlődnek."
        itemName={travel.eventTitle}
      />
    </Dialog>
  );
};

export default TravelEditorModal;
